import { getCoprincipalAccountForMailboxInfo, type CoprincipalAccountSource, } from "owa-account-source-list-store";
import getCoprincipalAccounts, { ContractsSupportedFilter, StartupFilter, } from "owa-account-source-list-store/lib/selectors/getCoprincipalAccounts";
import type { MailboxInfo } from "owa-client-types";
import { getMailboxInfoForGroup } from "./getMailboxInfoForGroup";
import { errorThatWillCauseAlertAndThrow } from "owa-trace";
import type { TraceErrorObject } from "owa-analytics-types";
import getAccountDiagnosticDataForMailboxInfo from "owa-account-source-list-store/lib/utils/getAccountDiagnosticDataForMailboxInfo";

export function getMailboxInfoFromDeeplinkParams(accountAlias: string | undefined, accountDomain: string | undefined, groupAlias: string | undefined, groupDomain: string | undefined, defaultAccountMailboxInfo: MailboxInfo): MailboxInfo {
    const targetAccount = findMatchingAccount(accountAlias, accountDomain) ??
        getCoprincipalAccountForMailboxInfo(defaultAccountMailboxInfo);
    
    if (!targetAccount) {
        const error: TraceErrorObject = new Error("No matching account found for deeplink params");
        error.additionalInfo = {
            hasAccountAlias: !!accountAlias,
            hasAccountDomain: !!accountDomain,
            hasGroupAlias: !!groupAlias,
            hasGroupDomain: !!groupDomain,
            ...getAccountDiagnosticDataForMailboxInfo(defaultAccountMailboxInfo),
        };
        errorThatWillCauseAlertAndThrow(error);
    }
    
    if (groupAlias && groupDomain) {
        // Deeplink is for a group mailbox under the matched account
        const groupEmailAddress = buildEmailAddress(groupAlias, groupDomain);
        return getMailboxInfoForGroup(targetAccount.mailboxInfo, groupEmailAddress);
    }
    
    return targetAccount.mailboxInfo;
}

function findMatchingAccount(accountAlias: string | undefined, accountDomain: string | undefined): CoprincipalAccountSource | undefined {
    if (!accountAlias || !accountDomain) {
        // We don't have the necessary account hints, so just return the fallback account if provided
        return undefined;
    }
    
    const targetEmailAddress = buildEmailAddress(accountAlias, accountDomain);
    const allAccounts = getCoprincipalAccounts(StartupFilter.StartingOrCompleteOrError, ContractsSupportedFilter.Any);
    
    /**
     * The order of preference for matching the account is:
     * 1. Match on email address or alias
     * 2. Match on user identity -- we only do this if we find we don't match in (1) to try to avoid ambiguity issues around shared mailboxes as accounts
     */
    return (allAccounts.find(account => doesAccountMatchEmailAddress(account, targetEmailAddress)) ??
        allAccounts.find(account => doesAccountMatchUserIdentity(account, targetEmailAddress)));
}

function buildEmailAddress(alias: string, domain: string): string {
    return `${alias}@${domain}`;
}

function doesAccountMatchEmailAddress(account: CoprincipalAccountSource, emailAddress: string): boolean {
    if (account.mailboxInfo.mailboxSmtpAddress === emailAddress) {
        return true;
    }
    
    if (account.aliases.includes(emailAddress)) {
        return true;
    }
    
    return false;
}

function doesAccountMatchUserIdentity(account: CoprincipalAccountSource, userIdentity: string): boolean {
    return account.mailboxInfo.userIdentity === userIdentity;
}
